/**
 * Creator Workflow Types
 * TypeScript type definitions for the Creator Workflow system
 */

export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export type ContentType =
  | 'dialogue'
  | 'article'
  | 'news'
  | 'speech'
  | 'advertisement'
  | 'story';

export type WorkflowStage =
  | 'material_input'          // Step 0: Upload existing materials (audio, text)
  | 'subtitle_editing'        // Step 0.5: Edit transcribed subtitles (for audio uploads)
  | 'conversational_editing'  // Step 0.5b: NL conversational editing (conversational mode)
  | 'generation'              // Step 1: Sonnet 4.5 generates content (or display uploaded)
  | 'sentence_editing'        // Step 1.5: Review & edit AI-segmented sentences (text workflow)
  | 'parsing'                 // Step 2: Haiku 4.5 analyzes content
  | 'audio_generation'        // Step 3: TTS generates per-sentence audio & merges (or split uploaded audio)
  | 'validation'              // Step 4: Backend validates content
  | 'publishing';             // Step 5: Publish to courses database

// Material source type - indicates what materials the creator provided
export type MaterialSourceType =
  | 'ai_generated'            // Path G: No materials provided, AI generates everything
  | 'audio_en_translation'    // Path A: Audio + English + Translation
  | 'audio_en'                // Path B: Audio + English only
  | 'audio_translation'       // Path C: Audio + Translation only
  | 'audio_only'              // Path D: Audio only
  | 'text_en'                 // Path E: English text only
  | 'text_translation';       // Path F: Translation text only

// Original 6 voices (tts-1, tts-1-hd compatible)
export type TTSVoiceLegacy = 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';

// New 3 voices (gpt-4o-mini-tts only)
// Note: ballad and verse are NOT actually supported by OpenAI API
export type TTSVoiceNew = 'ash' | 'coral' | 'sage';

// All 9 voices
export type TTSVoice = TTSVoiceLegacy | TTSVoiceNew;

export type TTSModel = 'tts-1' | 'tts-1-hd' | 'gpt-4o-mini-tts';

// Voice metadata for UI display
export interface VoiceMetadata {
  name: TTSVoice;
  displayName: string;
  gender: 'male' | 'female' | 'neutral';
  description: string;
  modelSupport: TTSModel[];
}

// Basic vocabulary entry (legacy/simple parser)
export interface VocabularyEntry {
  word: string;
  lemma?: string;
  pos?: string;
  tag?: string;
  difficulty?: number;
  frequency?: number;
  context?: string;
}

// Vocabulary quiz option
export interface VocabularyQuizOption {
  text: string;
  translation: string;
  is_correct: boolean;
}

// Vocabulary quiz
export interface VocabularyQuiz {
  question_en: string;
  question_native: string;
  options: VocabularyQuizOption[];
  explanation_en: string;
  explanation_native: string;
}

// Comprehensive vocabulary entry (AI parser)
export interface ComprehensiveVocabularyEntry {
  word: string;
  pronunciation: string; // IPA notation
  definition_in_context: string; // Specific meaning in this text (native language)
  general_definition_en: string; // General English definition
  general_definition: string; // General meaning in native language
  pos: 'noun' | 'verb' | 'adjective' | 'adverb' | 'other';
  difficulty: number; // 1-10
  frequency: 'common' | 'uncommon' | 'rare';
  context: string; // Sentence from text
  example_sentences: Array<{
    sentence_en: string;
    sentence_native: string;
  }>;
  synonyms: string[];
  antonyms: string[];
  collocations: string[];
  vocabulary_quiz: VocabularyQuiz; // Required quiz for this word
}

// Full text translation
export interface FullTextTranslation {
  translation: string;
}

// Visual annotation for pronunciation guidance
export interface SpeakingAnnotation {
  word: string;
  type: 'stress' | 'link' | 'pause' | 'reduction' | 'ipa' | 'syllables';
  hint?: string; // IPA for "ipa", reduced form for "reduction", syllable breakdown for "syllables"
}

// Speaking chunk for chunking strategy
export interface SpeakingChunk {
  text: string;
  translation: string;
  translations?: Record<string, string> | null;
  focus: string;
  focus_natives?: Record<string, string> | null;
  annotations?: SpeakingAnnotation[];
  intonation?: 'rising' | 'falling' | null;
  pause?: boolean;
}

// Speaking strategy for natural pronunciation
export interface SpeakingStrategy {
  chunks: SpeakingChunk[];
  tips_native: string;
  tips_natives?: Record<string, string> | null;
  tone?: string; // Emotional tone label (e.g., "Frustrated", "Reassuring")
}

/**
 * Sentence Analysis with Annotation-based Visual Effects
 *
 * Uses tokenization rules for synchronized text highlighting:
 * - Word = 1 token (e.g., "Hello" is token 0)
 * - Space = 1 token
 * - Punctuation + trailing space = 1 token combined
 *
 * Annotation format: "[effect startPos endPos startTime endTime]"
 * - effect: underline | highlight | hide_text | insert
 * - startPos/endPos: Token positions (0-indexed)
 * - startTime/endTime: Timing in seconds (relative to explanation audio)
 */
export interface SentenceAnalysis {
  annotations: string[]; // e.g., "[underline 0 4 0.0 3.0]", "[highlight 6 10 3.5 7.0]"
  explanation_native: string; // Native language explanation for TTS
  explanation_natives?: Record<string, string> | null;
  explanation_audio_url?: string; // Generated audio URL
  explanation_duration?: number; // Audio duration in seconds
}

/**
 * Dependency Graph - Visual Sentence Structure Analysis
 *
 * Provides an interactive visualization of sentence structure for learners.
 * Shows word roles, dependencies between words, and step-by-step explanations.
 */

// Single word node in the dependency graph
export interface DependencyWordNode {
  id: string;           // Unique identifier (e.g., 'subject', 'verb', 'object-det')
  word: string;         // The actual word text
  role: string;         // Grammar role in English (e.g., 'Subject', 'Verb', 'Object')
  role_native: string;  // Grammar role in native language (e.g., '主詞', '動詞', '受詞')
  role_natives?: Record<string, string> | null;
  color: string;        // Display color for highlighting (hex code)
}

// Dependency arc connecting two words
export interface DependencyArc {
  from: string;         // Source node ID
  to: string;           // Target node ID
  label: string;        // Relationship label in Chinese (e.g., '主詞', '受詞')
  label_natives?: Record<string, string> | null;
  label_en?: string;    // Relationship label in English (optional)
}

// Animation step for step-by-step explanation
export interface DependencyAnimationStep {
  node_ids: string[];   // Node IDs to highlight in this step
  explanation: string;  // Chinese explanation for this step
  explanation_natives?: Record<string, string> | null;
  duration?: number;    // Duration in milliseconds (default: 3500)
}

// Complete dependency graph data for a sentence
export interface DependencyGraphData {
  words: DependencyWordNode[];           // All word nodes (in sentence order)
  arcs: DependencyArc[];                 // All dependency connections
  animation_steps: DependencyAnimationStep[];  // Step-by-step explanation sequence
}

// Grammar point for a sentence
export interface GrammarPoint {
  explanation_en: string;
  explanation_native: string;
  explanation_natives?: Record<string, string> | null;
  example: string;
  example_native: string;
  example_natives?: Record<string, string> | null;
}

// Cultural note for a sentence
export interface CulturalNote {
  note_en: string;
  note_native: string;
  note_natives?: Record<string, string> | null;
}

// Sentence with translation and grammar analysis
export interface AnnotatedSentence {
  text: string;
  translation: string;
  translations?: Record<string, string> | null; // Multi-language: { "zh-TW": "...", "ja": "...", ... }
  speaker?: string | null;
  grammar_points?: Array<{
    explanation_en: string;
    explanation_native: string;
    explanation_natives?: Record<string, string> | null;
    example: string;
    example_native: string;
    example_natives?: Record<string, string> | null;
  }>;
  cultural_notes?: {
    note_en: string;
    note_native: string;
    note_natives?: Record<string, string> | null;
  };
  speaking_strategy?: SpeakingStrategy;
  dependency_graph?: DependencyGraphData; // Visual sentence structure analysis
  sentence_analysis?: SentenceAnalysis; // @deprecated - use dependency_graph instead
  key_vocabulary_in_sentence?: string[];
  // Audio data (populated from stage_progress.audio_generation.sentences)
  audio_url?: string;
  audio_duration?: number;
}

// Phrase types constant - centralized for reuse
export const PHRASE_TYPES = [
  'idiom',
  'phrasal_verb',
  'collocation',
  'expression',
  'slang',
  'formal',
  'informal',
] as const;

export type PhraseType = typeof PHRASE_TYPES[number];

// Phrase explanation
export interface PhraseEntry {
  text: string;
  type: PhraseType | string;
  definition_en: string;
  definition_native: string;
  definition_natives?: Record<string, string> | null;
  explanation_native?: string;
  explanation_natives?: Record<string, string> | null;
  origin?: string;
  usage_example?: string;
  usage_situation?: string;
}

// Quiz question
export interface QuizQuestion {
  id: string;
  type: 'multiple_choice' | 'fill_blank' | 'true_false' | 'matching';
  question_en: string;
  question_native: string;
  options?: Array<{
    text: string;
    translation: string;
  }>;
  correct_answer: string | string[];
  explanation_en: string;
  explanation_native: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  related_content?: string;
}

export interface PhraseChunk {
  text: string;
  type: string;
  root?: string;
  words?: string[];
}

export interface SentenceAudio {
  text: string;
  index: number;
  audioPath: string;
  audioUrl: string;
  duration?: number;
  speaker?: string;  // Detected speaker name (e.g., "Staff", "Passenger")
  voice: string;     // TTS voice used (e.g., "echo", "nova")
  voiceCharacterId?: string;  // Voice character ID (Gemini TTS)
  translation?: string; // Native language translation
  startTime?: number;         // Start time in merged audio (seconds)
  endTime?: number;           // End time in merged audio (seconds)
}

export interface StageProgress {
  material_input?: {
    started_at: string;
    completed: boolean;
    completed_at?: string;
    material_source?: MaterialSourceType;
    uploaded_audio_url?: string;
    uploaded_text_en?: string;
    uploaded_translation?: string;
  };
  generation?: {
    started_at: string;
    completed: boolean;
    completed_at?: string;
    processing_time_ms?: number;
    progress_percent?: number;     // Progress percentage (0-100)
    status_message?: string;       // Human-readable status message
    ai_metadata?: any;
  };
  parsing?: {
    started_at: string;
    completed: boolean;
    completed_at?: string;
    parser?: 'spacy' | 'rule-based';
    processing_time_ms?: number;
    progress_percent?: number;     // Progress percentage (0-100)
    current_item?: number;         // Current item being processed
    total_items?: number;          // Total items to process
    status_message?: string;       // Human-readable status message (fallback)
    status_key?: string;           // i18n translation key
    status_params?: Record<string, string | number>;  // i18n interpolation params
    parse_results?: {
      vocabulary_count: number;
      phrase_count: number;
      unique_words?: number;
      sentence_count: number;
      quiz_question_count?: number;
      word_count?: number;
      speaking_duration_seconds?: number | null;
      speaking_wpm?: number | null;
      estimated_cefr_level?: CEFRLevel;
      difficulty_metrics?: any;
    };
  };
  audio_generation?: {
    started_at: string;
    completed: boolean;
    completed_at?: string;
    processing_time_ms?: number;
    progress_percent?: number;     // Progress percentage (0-100)
    current_item?: number;         // Current sentence being generated
    total_items?: number;          // Total sentences to generate
    status_message?: string;       // Human-readable status message (fallback)
    status_key?: string;           // i18n translation key
    status_params?: Record<string, string | number>;  // i18n interpolation params
    sentences?: SentenceAudio[];   // Generated sentence audio files
    total_cost?: number;
    voice?: TTSVoice;              // For single-speaker audio
    model?: TTSModel;
    speed?: number;
    speakerVoiceMap?: Record<string, string>;  // For multi-speaker audio: speaker name -> voice
    // Merged audio data (previously in audio_merging, now stored directly here)
    merged_audio_url?: string;
    merged_audio_path?: string;
    subtitle_url_en?: string;      // English subtitles (.srt)
    subtitle_path_en?: string;
    subtitle_url_native?: string;   // Native language subtitles (.srt)
    subtitle_path_native?: string;
    merged_duration_seconds?: number;
    segmented_audio_data?: {       // Detailed data from segmented audio generation
      audio_type: 'segmented';
      total_sentences: number;
      total_cost: number;
      model: string;
      speed: number;
      voiceCharacterId?: string;   // Voice character ID for single-speaker mode
      speakerVoiceMap: Record<string, string>;  // Maps speaker name to voice character ID
      merged_audio_url: string;
      merged_audio_path: string;
      subtitle_url_en: string;
      subtitle_path_en: string;
      subtitle_url_native: string;
      subtitle_path_native: string;
      merged_duration_seconds: number;
      // Optimized pipeline metadata
      pipeline_type?: 'legacy' | 'optimized';  // 'optimized' uses speaker batching
      batch_count?: number;         // Number of speaker batches (fewer API calls)
      total_api_calls?: number;     // Total API calls made
    };
    error?: string;
    failed_at?: string;
  };
  validation?: {
    started_at: string;
    completed: boolean;
    completed_at?: string;
    processing_time_ms?: number;
    validation_result?: {
      is_valid: boolean;
      overall_score: number;
      passed_checks: number;
      total_checks: number;
      warnings_count: number;
      recommendations_count: number;
    };
    error?: string;
    failed_at?: string;
  };
  publishing?: {
    started_at: string;
    completed: boolean;
    completed_at?: string;
    processing_time_ms?: number;
    published_course_id?: string;
    error?: string;
    failed_at?: string;
  };
  explanation_audio?: {
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    total: number;
    completed: number;
    skipped: number;
    errors: number;
    started_at?: string;
    completed_at?: string;
    error?: string;
  };
}

export type ParsingStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

export interface CourseDraft {
  id: string;
  creator_id: string;
  current_stage: WorkflowStage;
  cefr_level: CEFRLevel;
  content_type: ContentType;
  tags?: string[];
  title?: string;
  title_native?: string;
  titles?: Record<string, string>;
  description?: string;
  target_word_count?: number;
  actual_word_count?: number;
  target_duration_seconds?: number;
  generated_text?: string;

  // Material source tracking (Stage 0)
  material_source?: MaterialSourceType;
  uploaded_audio_url?: string;
  uploaded_text_en?: string;
  uploaded_translation?: string;
  translation_language?: string;

  // Basic vocabulary (legacy parser)
  vocabulary?: VocabularyEntry[] | ComprehensiveVocabularyEntry[];
  phrase_chunks?: PhraseChunk[];

  // Comprehensive learning content (AI parser)
  full_text_translation?: FullTextTranslation;
  sentences?: AnnotatedSentence[];
  phrases?: PhraseEntry[];
  quiz_questions?: QuizQuestion[];

  // Conversational mode flags
  conversational_mode?: boolean;
  is_copyrighted_audio?: boolean;
  copyright_source?: string | null;
  copyright_acknowledged_at?: string | null;
  subtitle_editing_audio_url?: string;

  thumbnail_url?: string;
  audio_url?: string;
  subtitle_url_en?: string;      // English subtitles (.srt)
  subtitle_url_native?: string;   // Native language subtitles (.srt)
  stage_progress: StageProgress;
  parsing_status?: ParsingStatus;
  parsing_error?: string;
  version: number;
  last_saved_at?: Date;
  deleted_at?: Date;
  created_at: Date;
  updated_at: Date;

  // Publication status
  is_published?: boolean;           // Whether this draft has been published
  published_course_id?: string;     // ID of the published course (if published)

  // Set when this draft is a "shadow edit" of an already-published course.
  // Republish atomically swaps content back into the referenced course
  // instead of creating a new one.
  editing_published_course_id?: string;
}

export interface CreateDraftDto {
  cefr_level: CEFRLevel;
  content_type: ContentType;
  tags?: string[];
  target_word_count?: number;
  target_duration_seconds?: number;
  title?: string;
  description?: string;
}

export interface GenerateContentDto {
  cefr_level: CEFRLevel;
  content_type: ContentType;
  target_word_count?: number;
  tags?: string[];
  topic: string;
  scenario?: string;
  custom_requirements?: string;
}

export interface GenerateAudioDto {
  // Voice Character ID for single-speaker mode (e.g., 'Puck', 'Kore', 'Charon')
  voiceCharacterId?: string;
  // Legacy OpenAI voice (for backward compatibility)
  voice?: TTSVoice;
  model?: TTSModel;
  speed?: number;
  // Maps speaker name to voice character ID for multi-speaker mode
  speakerVoiceMap?: Record<string, string>;
}

export interface ValidationCheck {
  passed: boolean;
  message: string;
  score?: number;
}

export interface ValidationResult {
  isValid: boolean;
  checks: {
    hasContent: ValidationCheck;
    hasCefrLevel: ValidationCheck;
    hasVocabulary: ValidationCheck;
    hasPhrases: ValidationCheck;
    hasAudio: ValidationCheck;
    contentQuality: ValidationCheck;
  };
  warnings: string[];
  recommendations: string[];
  overallScore: number;
}

export interface ValidationResponse {
  draft: CourseDraft;
  validation: ValidationResult;
}

export interface DraftListResponse {
  data: CourseDraft[];
  total: number;
  page: number;
  limit: number;
}

export interface ParsingStatusResponse {
  status: ParsingStatus;
  error?: string;
  current_stage: WorkflowStage;
  vocabulary_count: number;
  phrase_count: number;
  quiz_count: number;
}
